import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft,
  FloppyDisk,
  Plus,
  Trash,
  X,
  DotsSixVertical,
  Clock,
  Tag,
  Play
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import FileAttachments from '../../components/FileAttachments';

const ProtocolForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps: [],
    materials: [],
    duration_minutes: '',
    tags: []
  });

  useEffect(() => {
    if (isEdit && id) {
      loadProtocol();
    }
  }, [id, isEdit]);

  const loadProtocol = async () => {
    try {
      const data = await api.getProtocol(id);
      setFormData(data);
    } catch (error) {
      console.error('Failed to load protocol:', error);
      toast.error('Failed to load protocol');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, { instruction: '', duration_minutes: '', notes: '' }]
    });
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const handleRemoveStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData({ ...formData, steps: newSteps });
  };

  const handleAddMaterial = () => {
    setFormData({ ...formData, materials: [...formData.materials, ''] });
  };

  const handleMaterialChange = (index, value) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = value;
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleRemoveMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const submitData = {
      ...formData,
      duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null
    };

    try {
      if (isEdit) {
        await api.updateProtocol(id, submitData);
        toast.success('Protocol updated');
      } else {
        const result = await api.createProtocol(submitData);
        toast.success('Protocol created');
        navigate(`/protocols/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save protocol');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="protocol-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/protocols" className="p-2 hover:bg-zinc-100 rounded-sm">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="heading-display">{isEdit ? 'Edit Protocol' : 'New Protocol'}</h1>
          <p className="text-zinc-500 mt-1">{isEdit ? 'Update protocol details' : 'Create a new protocol'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Untitled Protocol"
                  className="input-field w-full mt-1"
                  data-testid="protocol-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Protocol description..."
                  className="input-field w-full mt-1 min-h-[100px]"
                  data-testid="protocol-description-input"
                />
              </div>

              <div>
                <Label htmlFor="duration_minutes">Estimated Duration (minutes)</Label>
                <Input
                  id="duration_minutes"
                  name="duration_minutes"
                  type="number"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  placeholder="60"
                  className="input-field w-full mt-1 max-w-xs"
                  data-testid="protocol-duration-input"
                />
              </div>
            </div>

            {/* Steps */}
            <div className="card-flat p-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base">Protocol Steps</Label>
                <Button type="button" variant="outline" size="sm" className="btn-secondary" onClick={handleAddStep}>
                  <Plus size={14} className="mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-4">
                {formData.steps.map((step, idx) => (
                  <div key={idx} className="flex gap-3 p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="flex flex-col items-center gap-2">
                      <DotsSixVertical size={16} className="text-zinc-400 cursor-move" />
                      <div className="protocol-step-number">{idx + 1}</div>
                    </div>
                    <div className="flex-1 space-y-3">
                      <Textarea
                        value={step.instruction}
                        onChange={(e) => handleStepChange(idx, 'instruction', e.target.value)}
                        placeholder="Step instruction..."
                        className="input-field w-full min-h-[80px]"
                      />
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label className="text-xs">Duration (min)</Label>
                          <Input
                            type="number"
                            value={step.duration_minutes || ''}
                            onChange={(e) => handleStepChange(idx, 'duration_minutes', e.target.value)}
                            placeholder="5"
                            className="input-field w-full mt-1"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">Notes</Label>
                          <Input
                            value={step.notes || ''}
                            onChange={(e) => handleStepChange(idx, 'notes', e.target.value)}
                            placeholder="Optional notes"
                            className="input-field w-full mt-1"
                          />
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={() => handleRemoveStep(idx)} className="p-1 hover:bg-zinc-200 rounded-sm h-fit">
                      <X size={16} className="text-zinc-500" />
                    </button>
                  </div>
                ))}
                {formData.steps.length === 0 && (
                  <p className="text-sm text-zinc-500 text-center py-4">No steps added yet. Click "Add Step" to begin.</p>
                )}
              </div>
            </div>

            {/* Materials */}
            <div className="card-flat p-6">
              <div className="flex items-center justify-between mb-4">
                <Label>Materials Required</Label>
                <Button type="button" variant="outline" size="sm" className="btn-secondary" onClick={handleAddMaterial}>
                  <Plus size={14} className="mr-1" /> Add Material
                </Button>
              </div>
              <div className="space-y-2">
                {formData.materials.map((material, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={material}
                      onChange={(e) => handleMaterialChange(idx, e.target.value)}
                      placeholder="Material name"
                      className="input-field flex-1"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveMaterial(idx)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                {formData.materials.length === 0 && (
                  <p className="text-sm text-zinc-500">No materials added yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tags */}
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag"
                  className="input-field flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  <Tag size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-protocol-btn">
              {saving ? (
                <>
                  <div className="spinner w-4 h-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk size={16} className="mr-2" />
                  {isEdit ? 'Update Protocol' : 'Create Protocol'}
                </>
              )}
            </Button>

            {/* Execute Button (edit mode only) */}
            {isEdit && id && formData.steps?.length > 0 && (
              <Link to={`/protocols/${id}/bench`} className="block">
                <Button type="button" variant="outline" className="w-full py-3 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all" data-testid="execute-protocol-btn">
                  <Play size={16} weight="fill" className="mr-2" />
                  Execute Protocol
                </Button>
              </Link>
            )}
          </div>
        </div>
      </form>

      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="protocols" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default ProtocolForm;
